import React, { useEffect, useMemo, useState } from 'react';
import MonsterStatPanel from './MonsterStatPanel';
import StatRuneSwitch from './StatRuneSwitch';
import RuneBuildPanel from './RuneBuildPanel';
import { preloadRuneImages } from './RuneStar';
import { Rune, Unit, runesFromAccount, unitsFromAccount, ATTRIBUTE_KEY } from './runeEngine';
import { TacticBuild, buildFromReplayUnit } from './TacticBuild';

/**
 * **그 전투에서 쓴 룬** — 전투기록 창에서 몹을 누르면 그 몹의 능력치와 룬을 보여 준다.
 *
 * 룬은 점령전이 끝난 뒤 에이전트가 공격 기록마다 리플레이를 열어 받아 온 것이다
 * (`GET /api/siege-battles/build/:logId`). 그 전투 당시의 세팅이라 지금 계정과 다를 수 있다.
 *
 * ## 옵티마이저와 같은 셈
 *
 * 리플레이의 몹 원본은 SWEX 계정 JSON 의 `unit_list[]` 한 칸과 **같은 모양**이다. 그래서 한 칸짜리
 * 계정을 만들어 룬 옵티마이저의 `runesFromAccount`·`unitsFromAccount` 에 그대로 넣는다 — 같은 룬이
 * 화면마다 다르게 셈해지면 안 된다. 그리는 것도 옵티마이저의 `MonsterStatPanel`·`RuneBuildPanel`
 * 그대로다(사용자 지시: 옵티마이저 디자인 그대로).
 *
 * **아티팩트도 보인다**(사용자 지시) — 별 왼쪽의 둥근 칸 둘, 그리고 능력치에도 실제 아티팩트
 * 주옵을 넣는다(옵티마이저는 늘 공격력 +200 으로 보지만 여기서는 그 전투에 낀 것을 안다).
 *
 * 원본을 함께 저장하기 전(2026-10-09 오후)에 받은 기록은 저장된 값으로 원본 모양을 다시 만든다 —
 * 룬·아티팩트는 같게 나오지만 **유물은 없다.** 리플레이에는 치명 확률·치명 피해·효과 적중이 없어서
 * 그 셋은 기본값(15·50·0)으로 본다.
 */

export type RunePick = { side: 'attack' | 'defense'; idx: number };

export interface SavedUnit {
    unitMasterId: number;
    pos: number;
    level: number;
    stars: number;
    base: { con: number; atk: number; def: number; spd: number; resist: number };
    runes: Array<{ slot: number; set: number; stars: number; rank: number; extra: number; level: number; main: number[]; innate: number[]; subs: number[][] }>;
    artifacts?: Array<{ slot: number; type: number; attribute: number; unitStyle: number; rank: number; level: number; main: number[]; subs: number[][] }>;
    raw?: any;
}
interface SavedSide { wizardName: string; units: SavedUnit[] }
interface Build {
    found: boolean; attack?: SavedSide | null; defense?: SavedSide | null; opponentHidden?: boolean;
    /** 방어 기록 — 리플레이가 없어 **받아 둔 우리 방덱 룬**으로 우리 쪽만 채웠다(상대 공덱은 없다) */
    fromDeck?: boolean;
    capturedAt?: string;
}

/**
 * **지금 로그인한 사람이 상대 덱의 룬을 볼 수 있나** — 계정의 `opponentRunes`(애플톡이 계정 관리창에서
 * 켜고 끈다). 실제로 막는 것은 서버다. 여기는 상대 쪽 아이콘을 누를 수 있게 할지만 정한다.
 */
export function useCanSeeOpponentRunes(): boolean {
    const [on, setOn] = useState(false);
    useEffect(() => {
        let alive = true;
        fetch('/api/auth/check', { credentials: 'include' })
            .then((r) => r.json())
            .then((d) => { if (alive) setOn(!!d?.loggedIn && !!d.opponentRunes); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);
    return on;
}

/**
 * 전투 한 건의 열쇠 — `<siegeId>-<logType>-<logId>`(서버 utils/battleRef). 게임의 전투 번호(logId)는 점령전이 바뀌면
 * 다시 나와서 번호만으로는 다른 전투가 열린다. 예전처럼 번호만 주면 서버가 그 번호의 가장 최근 전투를 고른다.
 */
export type BattleKey = string | number;

/** 창을 열고 닫거나 몹을 바꿀 때마다 다시 받지 않게 — 전투 하나에 한 번 */
const cache = new Map<BattleKey, Promise<Build>>();
function loadBuild(logId: BattleKey): Promise<Build> {
    let p = cache.get(logId);
    if (!p) {
        p = fetch(`/api/siege-battles/build/${logId}`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : { found: false }))
            .catch(() => ({ found: false }));
        // 아직 없던 것은 담아 두지 않는다 — 다음에 열 때는 받아 두었을 수 있다
        // 상대 쪽을 가린 답도 담지 않는다 — 그 사이 볼 수 있는 사람으로 로그인했을 수 있다
        p.then((b) => { if (!b.found || b.opponentHidden) cache.delete(logId); });
        cache.set(logId, p);
    }
    return p;
}

/** 게임 몹 번호의 끝자리가 속성이다 (1 물 · 2 불 · 3 바람 · 4 빛 · 5 어둠) */
const elementOf = (unitMasterId: number) => unitMasterId % 10;

/** 저장된 값으로 SWEX `unit_list[]` 한 칸 모양을 다시 만든다 (원본이 없을 때) */
export function rawOf(u: SavedUnit, n: number): any {
    if (u.raw) return { ...u.raw, unit_id: u.raw.unit_id || n, attribute: u.raw.attribute || elementOf(u.unitMasterId) };
    return {
        unit_id: n,
        unit_master_id: u.unitMasterId,
        unit_level: u.level,
        class: u.stars,
        attribute: elementOf(u.unitMasterId),
        con: u.base.con, atk: u.base.atk, def: u.base.def, spd: u.base.spd, resist: u.base.resist,
        runes: u.runes.map((r, i) => ({
            rune_id: i + 1, slot_no: r.slot, set_id: r.set, class: r.stars, rank: r.rank, extra: r.extra,
            upgrade_curr: r.level, pri_eff: r.main, prefix_eff: r.innate, sec_eff: r.subs
        })),
        artifacts: (u.artifacts || []).map((a, i) => ({
            rid: i + 1, slot: a.slot, type: a.type, attribute: a.attribute, unit_style: a.unitStyle,
            rank: a.rank, level: a.level, pri_effect: a.main, sec_effects: a.subs
        })),
        relics: []
    };
}

/**
 * 고른 자리의 몹. 게시물의 팀은 덱 순서라 **같은 순서**로 짝짓되, 속성이 어긋나면 속성이 맞는
 * 몹을 고른다(덱 순서가 혹시 다르게 들어와도 엉뚱한 몹의 룬을 보이지 않게).
 */
function unitAt(side: SavedSide | null | undefined, idx: number, attribute?: string): SavedUnit | null {
    const units = side?.units || [];
    if (!units.length) return null;
    const want = Number(Object.keys(ATTRIBUTE_KEY).find((k) => ATTRIBUTE_KEY[Number(k)] === attribute));
    const at = units[idx];
    if (!want || (at && elementOf(at.unitMasterId) === want)) return at || null;
    return units.find((u) => elementOf(u.unitMasterId) === want) || at || null;
}

/**
 * **이 전투의 룬으로 공략 세팅 여섯 칸을 만든다** — 전투기록에서 "공략으로 쓰기" 로 열 때.
 *
 * 칸 순서는 게시물의 덱 순서(공덱 셋, 방덱 셋)다. 아직 룬을 받지 않은 전투면 여섯 칸 모두 null 이고,
 * 사람이 쓰기 창에서 직접 적으면 된다. 원본이 없는 옛 기록도 저장된 값으로 원본 모양을 다시 만들어 셈한다.
 */
export async function battleBuilds(
    logId: BattleKey,
    attackTeam: Array<{ attribute?: string } | null>,
    defenseTeam: Array<{ attribute?: string } | null>
): Promise<Array<TacticBuild | null>> {
    const b = await loadBuild(logId);
    const none = [null, null, null, null, null, null];
    if (!b.found) return none;
    const side = (s: SavedSide | null | undefined, team: Array<{ attribute?: string } | null>) =>
        [0, 1, 2].map((i) => {
            const u = unitAt(s, i, team[i]?.attribute);
            return u ? buildFromReplayUnit(rawOf(u, i + 1)) : null;
        });
    return [...side(b.attack, attackTeam || []), ...side(b.defense, defenseTeam || [])];
}

export default function BattleRunes({ logId, pick, defenseLog, attribute, name, img, isMobile }: {
    /** 전투 열쇠(`BattleKey`) */
    logId: BattleKey;
    /** 창 위쪽 아이콘에서 고른 자리 (왼쪽이 attack) */
    pick: RunePick;
    /** 방어 기록이면 우리 덱이 왼쪽(attack 자리)에 옮겨져 있다 — 룬은 방어 쪽에서 찾는다 */
    defenseLog?: boolean;
    /** 고른 몹의 속성 (`Water` …) — 짝을 맞출 때 */
    attribute?: string;
    name: string;
    img: string | null;
    isMobile: boolean;
}) {
    const [build, setBuild] = useState<Build | null>(null);

    useEffect(() => {
        let alive = true;
        setBuild(null);
        preloadRuneImages();
        loadBuild(logId).then((b) => { if (alive) setBuild(b); });
        return () => { alive = false; };
    }, [logId]);

    const picked = useMemo(() => {
        if (!build?.found) return null;
        const side = defenseLog ? (pick.side === 'attack' ? build.defense : build.attack) : build[pick.side];
        const u = unitAt(side, pick.idx, attribute);
        if (!u) return null;
        // 한 칸짜리 계정 — 옵티마이저 셈을 그대로 쓴다
        const account = { unit_list: [rawOf(u, 1)] };
        const unit: Unit | null = unitsFromAccount(account)[0] || null;
        const runes: Rune[] = runesFromAccount(account).runes.sort((a, b) => a.slot - b.slot);
        return { unit, runes };
    }, [build, pick, defenseLog, attribute]);

    const note: React.CSSProperties = {
        padding: isMobile ? '18px 10px' : '26px 12px', textAlign: 'center',
        fontSize: isMobile ? 12 : 13, fontWeight: 600, color: '#999'
    };

    if (!build) return <div style={note}>룬을 불러오는 중…</div>;
    if (!build.found) return <div style={note}>이 전투의 룬은 아직 기록되지 않았습니다.</div>;
    // 고른 몹이 들어 있는 쪽 — 방어 기록이면 왼쪽(attack 자리)이 우리 방어 덱이다
    const pickedSide = defenseLog ? (pick.side === 'attack' ? build.defense : build.attack) : build[pick.side];
    if (build.opponentHidden && !pickedSide) {
        return <div style={note}>상대 덱의 룬은 볼 수 없습니다.</div>;
    }
    if (build.fromDeck && !pickedSide) {
        return <div style={note}>방어 기록은 리플레이가 없어 상대 공덱의 룬은 받을 수 없습니다.</div>;
    }
    if (!picked) return <div style={note}>이 몹의 룬을 찾지 못했습니다.</div>;

    return (
        <div style={{ width: '100%' }}>
            {build.fromDeck && build.capturedAt && (
                // 전투 순간의 룬이 아니라 [전투 정보] 에서 받은 그때의 방덱 룬이다
                <div style={{ fontSize: isMobile ? 11 : 12, color: '#999', fontWeight: 600, textAlign: 'right', marginBottom: 4 }}>
                    방덱 룬 · {new Date(build.capturedAt).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })} 기준
                </div>
            )}
            <StatRuneSwitch
                isMobile={isMobile} fitHeight
                stat={
                    <MonsterStatPanel
                        unit={picked.unit} runes={picked.runes} artifacts={picked.unit?.artifacts || []}
                        name={name} img={img} isMobile={isMobile}
                    />
                }
                rune={<>
                    {/* 몹을 바꾸면 고른 룬 칸도 비운다 — 같은 자리 번호라도 다른 룬이다 */}
                    {/* 그 전투에 쓴 룬 그대로 — 변환·연마 여지는 보이지 않는다(사용자 지시) */}
                    <RuneBuildPanel
                        key={`${pick.side}-${pick.idx}`}
                        runes={picked.runes} objective="power"
                        relic={picked.unit?.relics[0] || null} artifacts={picked.unit?.artifacts || []}
                        isMobile={isMobile} asIs initialPick={1}
                    />
                </>}
            />
        </div>
    );
}
